import React, { useState } from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';
import { 
  mockPrivilegedRoles, 
  mockPIMAlerts, 
  mockPIMAuditEvents 
} from '../../utils/mockAzureEntraData';
import { PrivilegedRole, PIMAlert, PIMAuditEvent } from '../../types/azureEntraId';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${Spacing.xl}px;
  height: 100%;
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl}px;
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

const PIMHeader = styled.div`
  background: linear-gradient(135deg, #ca5010 0%, #8764b8 100%);
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  color: white;
  margin-bottom: ${Spacing.xl}px;
`;

const PIMTitle = styled.h1`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  margin: 0 0 ${Spacing.sm}px 0;
`;

const PIMSubtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: ${FontSizes.md}px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

const StatCard = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${Colors.borderLight};
`;

const StatValue = styled.div`
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const StatLabel = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardContainer = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  background: ${Colors.backgroundDark};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return '#ca5010';
      case 'danger': return Colors.danger;
      default: return Colors.surface;
    }
  }};
  color: ${props => props.variant ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.variant ? 'transparent' : Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const RoleCard = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const RoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${Spacing.md}px;
`;

const RoleName = styled.h4`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.xs}px 0;
`;

const RoleDescription = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const StatusBadge = styled.span<{ status: 'active' | 'eligible' | 'expired' | 'high' | 'medium' | 'low' }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  background: ${props => {
    switch (props.status) {
      case 'active': return Colors.success;
      case 'eligible': return Colors.info;
      case 'expired': return Colors.borderDark;
      case 'high': return Colors.danger;
      case 'medium': return Colors.warning;
      case 'low': return Colors.info;
      default: return Colors.borderLight;
    }
  }};
  color: ${Colors.textOnPrimary};
`;

const AssignmentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md}px;
  margin-top: ${Spacing.md}px;
`;

const AssignmentSection = styled.div`
  padding: ${Spacing.md}px;
  background: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
`;

const AssignmentTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.sm}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AssignmentCount = styled.div`
  font-size: ${FontSizes.xl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
`;

const AlertCard = styled.div<{ severity: 'High' | 'Medium' | 'Low' }>`
  padding: ${Spacing.md}px;
  border-left: 4px solid ${props => {
    switch (props.severity) {
      case 'High': return Colors.danger;
      case 'Medium': return Colors.warning;
      case 'Low': return Colors.info;
    }
  }};
  background: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.sm}px;
  margin-bottom: ${Spacing.sm}px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AlertTitle = styled.h5`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.xs}px 0;
`;

const AlertDescription = styled.p`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  margin: 0 0 ${Spacing.xs}px 0;
  line-height: 1.4;
`;

const AlertMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AlertTime = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const AuditEventCard = styled.div`
  padding: ${Spacing.md}px;
  border-bottom: 1px solid ${Colors.borderLight};
  
  &:last-child {
    border-bottom: none;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: start;
  margin-bottom: ${Spacing.sm}px;
`;

const EventTitle = styled.h5`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin: 0;
  flex: 1;
`;

const EventTime = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const EventDetail = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.xs}px;
`;

const EventUser = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textPrimary};
  font-weight: ${FontWeights.medium};
`;

const PrivilegedIdentityManagement: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const totalActiveAssignments = mockPrivilegedRoles.reduce((sum, role) => 
    sum + role.activeAssignments.length, 0
  );
  
  const totalEligibleAssignments = mockPrivilegedRoles.reduce((sum, role) => 
    sum + role.eligibleAssignments.length, 0
  );

  const highSeverityAlerts = mockPIMAlerts.filter(alert => 
    alert.severity === 'High' && alert.isActive
  ).length;

  return (
    <>
      <PIMHeader>
        <PIMTitle>Privileged Identity Management</PIMTitle>
        <PIMSubtitle>
          Just-in-time privileged access to Azure AD and Azure resources
        </PIMSubtitle>
      </PIMHeader>

      <StatsRow>
        <StatCard>
          <StatValue>{mockPrivilegedRoles.length}</StatValue>
          <StatLabel>Privileged Roles</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalActiveAssignments}</StatValue>
          <StatLabel>Active Assignments</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalEligibleAssignments}</StatValue>
          <StatLabel>Eligible Assignments</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{highSeverityAlerts}</StatValue>
          <StatLabel>Critical Alerts</StatLabel>
        </StatCard>
      </StatsRow>

      <Container>
        <MainContent>
          <CardContainer>
            <CardHeader>
              <CardTitle>Privileged Roles</CardTitle>
              <ActionButton variant="primary">Add Role Assignment</ActionButton>
            </CardHeader>
            <div>
              {mockPrivilegedRoles.map(role => (
                <RoleCard key={role.id}>
                  <RoleHeader>
                    <div>
                      <RoleName>{role.displayName}</RoleName>
                      <RoleDescription>{role.description}</RoleDescription>
                    </div>
                    <StatusBadge status={role.isEnabled ? 'active' : 'expired'}>
                      {role.isEnabled ? 'Enabled' : 'Disabled'}
                    </StatusBadge>
                  </RoleHeader>
                  
                  <AssignmentGrid>
                    <AssignmentSection>
                      <AssignmentTitle>Active Assignments</AssignmentTitle>
                      <AssignmentCount>{role.activeAssignments.length}</AssignmentCount>
                    </AssignmentSection>
                    <AssignmentSection>
                      <AssignmentTitle>Eligible Assignments</AssignmentTitle>
                      <AssignmentCount>{role.eligibleAssignments.length}</AssignmentCount>
                    </AssignmentSection>
                  </AssignmentGrid>
                  
                  <div style={{ marginTop: `${Spacing.md}px`, display: 'flex', gap: `${Spacing.sm}px` }}>
                    <ActionButton>View Assignments</ActionButton>
                    <ActionButton>Configure Settings</ActionButton>
                    <ActionButton>Access Reviews</ActionButton>
                  </div>
                </RoleCard>
              ))}
            </div>
          </CardContainer>

          <CardContainer>
            <CardHeader>
              <CardTitle>Recent Audit Events</CardTitle>
              <ActionButton>View All Events</ActionButton>
            </CardHeader>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {mockPIMAuditEvents.map(event => (
                <AuditEventCard key={event.id}>
                  <EventHeader>
                    <EventTitle>{event.activityDisplayName}</EventTitle>
                    <EventTime>{formatTimeAgo(event.activityDateTime)}</EventTime>
                  </EventHeader>
                  
                  <EventDetail>
                    <strong>Target:</strong> {event.targetResources[0]?.displayName}
                  </EventDetail>
                  
                  {event.initiatedBy.user && (
                    <EventUser>
                      By: {event.initiatedBy.user.displayName}
                    </EventUser>
                  )}
                  
                  {event.additionalDetails && event.additionalDetails.length > 0 && (
                    <EventDetail>
                      {event.additionalDetails.map(detail => (
                        <div key={detail.key}>
                          <strong>{detail.key}:</strong> {detail.value}
                        </div>
                      ))}
                    </EventDetail>
                  )}
                  
                  <StatusBadge status={event.result === 'success' ? 'active' : 'expired'}>
                    {event.result}
                  </StatusBadge>
                </AuditEventCard>
              ))}
            </div>
          </CardContainer>
        </MainContent>

        <SidePanel>
          <CardContainer>
            <CardHeader>
              <CardTitle>PIM Alerts</CardTitle>
              <ActionButton>Configure Alerts</ActionButton>
            </CardHeader>
            <div style={{ padding: `${Spacing.md}px` }}>
              {mockPIMAlerts.filter(alert => alert.isActive).map(alert => (
                <AlertCard key={alert.id} severity={alert.severity}>
                  <AlertTitle>{alert.alertName}</AlertTitle>
                  <AlertDescription>
                    {alert.incidentCount} incidents detected
                  </AlertDescription>
                  <AlertMeta>
                    <StatusBadge status={alert.severity.toLowerCase() as 'high' | 'medium' | 'low'}>
                      {alert.severity}
                    </StatusBadge>
                    <AlertTime>{formatTimeAgo(alert.lastScannedDateTime)}</AlertTime>
                  </AlertMeta>
                </AlertCard>
              ))}
            </div>
          </CardContainer>

          <CardContainer>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <div style={{ padding: `${Spacing.md}px`, display: 'flex', flexDirection: 'column', gap: `${Spacing.sm}px` }}>
              <ActionButton variant="primary">Activate Role</ActionButton>
              <ActionButton>View My Roles</ActionButton>
              <ActionButton>Request Access</ActionButton>
              <ActionButton>Review Assignments</ActionButton>
              <ActionButton>Generate Report</ActionButton>
            </div>
          </CardContainer>

          <CardContainer>
            <CardHeader>
              <CardTitle>Role Settings</CardTitle>
            </CardHeader>
            <div style={{ padding: `${Spacing.md}px` }}>
              <div style={{ marginBottom: `${Spacing.md}px` }}>
                <div style={{ fontSize: `${FontSizes.sm}px`, fontWeight: FontWeights.medium, marginBottom: `${Spacing.xs}px` }}>
                  Default Activation Duration
                </div>
                <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textSecondary }}>
                  4 hours
                </div>
              </div>
              
              <div style={{ marginBottom: `${Spacing.md}px` }}>
                <div style={{ fontSize: `${FontSizes.sm}px`, fontWeight: FontWeights.medium, marginBottom: `${Spacing.xs}px` }}>
                  MFA Required
                </div>
                <StatusBadge status="active">Enabled</StatusBadge>
              </div>
              
              <div style={{ marginBottom: `${Spacing.md}px` }}>
                <div style={{ fontSize: `${FontSizes.sm}px`, fontWeight: FontWeights.medium, marginBottom: `${Spacing.xs}px` }}>
                  Approval Required
                </div>
                <StatusBadge status="active">Enabled</StatusBadge>
              </div>
              
              <ActionButton>Modify Settings</ActionButton>
            </div>
          </CardContainer>
        </SidePanel>
      </Container>
    </>
  );
};

export default PrivilegedIdentityManagement;